import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import App from './App.vue';
import adsenseService from './services/adsenseService';
import { installAdBoundary, protectAdNavigation } from './utils/adBoundary';
import router from './router';
import i18n, { setLanguage } from './i18n';
import AlertNotification from './components/ui/AlertNotification.vue';
import { useAuthStore } from './store/auth';
import { identifyChatwootUser, startChatwoot } from './utils/chatwoot';

// Import both styles (Bulma with old import, our styles with new @use)
import './styles/main-bulma.scss';
import './styles/main.scss';
import './styles/refresh.css';
import './styles/landing.css';
import './styles/platform.css';
import './styles/workflows.css';
import './styles/product.css';
import './styles/artist-services.css';
import './styles/tool-studio.css';
import './styles/dark-mode.css';
import './styles/chatwoot.css';

// Create app
const app = createApp(App);

// Add global properties
app.provide('APP_NAME', 'Indiefy');
app.provide('APP_VERSION', '1.0.0');

// Use plugins
const pinia = createPinia();
const head = createHead();
app.use(pinia);
app.use(head);
app.use(router);
app.use(i18n);

// Register global components
app.component('AlertNotification', AlertNotification);

// Check for stored language, query parameter, or use browser language
const supportedLanguages = ['en', 'es', 'pt', 'tr', 'hi', 'fr'];
const storedLanguage = localStorage.getItem('indiefy-language');
const urlParams = new URLSearchParams(window.location.search);
const langParam = urlParams.get('lang');

if (supportedLanguages.includes(langParam)) {
  setLanguage(langParam);
} else if (supportedLanguages.includes(storedLanguage)) {
  setLanguage(storedLanguage);
} else {
  // Get browser language and clean it up
  let browserLang = window.navigator.userLanguage || window.navigator.language;
  browserLang = browserLang.split('-')[0].split('_')[0].toLowerCase();

  // Use browser language if supported, otherwise fallback to English
  const lang = supportedLanguages.includes(browserLang) ? browserLang : 'en';
  setLanguage(lang);
}

// Initialize theme from localStorage synchronously to prevent FOUC.
// Dark mode is a PRO-only feature, so we also check the cached user payload
// in localStorage before applying the classes. Otherwise a non-PRO user with
// a stale `indiefy-theme=dark` (e.g. after their subscription lapsed) would
// see the page render dark and then flash back to light once `App.vue`'s
// `themeStore.setDarkMode(true)` runs on mount.
const savedTheme = localStorage.getItem('indiefy-theme');
if (savedTheme === 'dark') {
  let canUseDarkMode = false;
  try {
    const storedUser = JSON.parse(localStorage.getItem('indiefy-user'));
    canUseDarkMode = !!(storedUser && storedUser.subscription);
  } catch (e) {
    canUseDarkMode = false;
  }

  if (canUseDarkMode) {
    document.documentElement.classList.add('dark-theme');
    document.body.classList.add('body--dark');
  } else {
    // Clear the stale preference so the store and storage stay in sync.
    localStorage.setItem('indiefy-theme', 'light');
  }
}

// The router owns session verification, including magic links and redirects.
// Boot support independently so its identity still waits for the live iframe.
const authStore = useAuthStore();
if (authStore.user) identifyChatwootUser(authStore.user);
else startChatwoot();
installAdBoundary(router, authStore, adsenseService);
document.getElementById('indiefy-startup')?.setAttribute(
  'aria-label', i18n.global.t('general.loading'),
);

// Wait for the router to resolve the initial route (including async guards
// and dynamic component imports) before mounting. This prevents the
// "blink twice" effect where `<router-view>` first renders empty (no
// component resolved yet) and then re-renders with the resolved component,
// triggering the page transition's enter animation a second time.
router.isReady().then(async () => {
  app.mount('#app');
  // Theme and route state settle before revealing the first app frame.
  await nextTick();
  document.documentElement.classList.remove('indiefy-booting');
  protectAdNavigation(document.getElementById('app'), router);
});
